import React from "react";
import PropTypes from "prop-types";
import { Form, Formik } from "formik";
import { Button } from "reactstrap";
import classNames from "classnames";
import { useMutation, useQuery } from "@apollo/react-hooks";

import LoaderOverlay from "shared/react_components/LoaderOverlay";
import Toast from "shared/toast";
import { withRedirectOnUnauthorized, withErrorMessage } from "shared/utils/graphQLErrorHelpers";
import {
  ControlledDrawer,
  DrawerHeader,
  DrawerFooter,
  DrawerBody
} from "shared/react_components/Drawer";

import EditUserFields from "./EditUserFields";
import { UpdateMember, GetMemberAndConfig, SendTwoFactorVerificationCode } from "./Members.graphql";
import { reducedErrorsFromResponse } from "../utilities/errors";
import schema from "../schemas/EditUserForm";

import Styles from "../styles/styles.module.scss";

const parseData = ({
  __typename, // omit this field from being passed to form
  id, // omit this field from being passed to form
  permissions, // omit this field from being passed to form
  notificationSettings,
  user: {
    __typename: userTypename, // omit this field from being passed to form
    twoFactorAuthRequired, // omit this field from being passed to form
    emails,
    ...user
  },
  reportFilters,
  userLicenses: {
    __typename: licenseTypename, // omit from being passed to form.
    ...userLicenses
  },
  generalNotificationSettings,
  bundledNotificationSettings,
  ...member
}) => ({
  ...member,
  user: {
    ...user,
    emails: emails.map(({ __typename, ...email }) => ({
      ...email,
      value: email.email,
      label: email.email,
      submitted: true
    }))
  },
  userLicenses,
  notificationSettings: notificationSettings.reduce(
    (settings, { name, value }) => ({ ...settings, [name]: value }),
    {}
  ),
  reportFilters: reportFilters.map(({ __typename, ...filters }) => ({
    ...filters
  }))
});

const formatData = ({ user: { emails, ...user }, notificationSettings, ...attributes }) => ({
  ...attributes,
  user: {
    ...user,
    emails: emails.map(({ value, label, submitted, verified, ...email }) => email)
  },
  notificationSettings: Object.keys(notificationSettings).map(name => ({
    name,
    value: notificationSettings[name]
  }))
});

const formatNotificationSettings = ({
  member: { generalNotificationSettings, bundledNotificationSettings },
  userManagementConfiguration: { nameForAdvertiser }
}) => ({
  generalPreferences: generalNotificationSettings,
  bundledPreferences: bundledNotificationSettings,
  nameForAdvertiser: nameForAdvertiser
});

const EditUserDrawer = ({ isOpen, memberID, onCancel, onSubmit, marketingDataDictionaryUrl }) => {
  const { data, loading: queryLoading } = useQuery(GetMemberAndConfig, {
    variables: { id: memberID }
  });
  const [updateMember, { loading: mutationLoading }] = useMutation(UpdateMember);
  const [requestTwoFactorCode] = useMutation(SendTwoFactorVerificationCode);

  const sendTwoFactorRequest = (number, onSubmitted, onError) => {
    requestTwoFactorCode({
      variables: {
        id: memberID,
        twoFactorAuthPhoneNumber: number
      }
    })
      .then(() => {
        onSubmitted();
        Toast.success(`Verification code sent to ${number}`);
      })
      .catch(({ graphQLErrors }) => {
        const [{ message }] = graphQLErrors;
        onError(message);
      });
  };

  const loading = queryLoading || mutationLoading;
  const { member } = data || { member: {} };

  return (
    <Formik
      enableReinitialize
      initialValues={Object.keys(member).length ? parseData(member) : member}
      validationSchema={schema}
      isInitialValid={({ initialValues, validationSchema }) =>
        validationSchema.isValidSync(initialValues)
      }
      onSubmit={(attributes, { setSubmitting, setErrors }) => {
        updateMember({
          variables: {
            id: memberID,
            attributes: formatData(attributes)
          }
        })
          .then(({ data }) => {
            const { updateMember } = data;
            const { errors } = updateMember;

            if (errors && errors.length) {
              const reducedErrors = reducedErrorsFromResponse(errors);
              const { base, emails, reportFilters } = reducedErrors;
              if (base) {
                Toast.error(base);
              }
              if (emails) {
                Toast.error({
                  title: "Errors saving email address",
                  message: emails
                });
              }
              if (reportFilters) {
                Toast.error({
                  title: "Errors saving report filters",
                  message: reportFilters
                });
              }
              setErrors(reducedErrors);
              setSubmitting(false);
            } else {
              Toast.success("User updated.");
              onSubmit();
            }
          })
          .catch(
            withRedirectOnUnauthorized(
              withErrorMessage(message => {
                Toast.error(message);
                setSubmitting(false);
              })
            )
          );
      }}
    >
      {({ initialValues, isSubmitting, isValid, values }) => (
        <Form className={"d-flex flex-column"}>
          <ControlledDrawer className={classNames(Styles.drawer)} isOpen={isOpen}>
            <DrawerHeader>
              <h3>Edit User</h3>
            </DrawerHeader>
            <DrawerBody>
              <LoaderOverlay loading={loading}>
                {!!Object.keys(initialValues).length && (
                  <EditUserFields
                    memberID={memberID}
                    timeZones={data.userManagementConfiguration.timeZoneSelectOptions}
                    roles={data.userManagementConfiguration.availableRoleSelectOptions}
                    rolePermissionsUrl={data.userManagementConfiguration.rolePermissionsUrl}
                    marketingDataDictionaryUrl={marketingDataDictionaryUrl}
                    userNotificationSettings={formatNotificationSettings(data)}
                    marketingDataCategories={
                      data.userManagementConfiguration.marketingDataCategories
                    }
                    permissions={ { ...member.permissions, ...data.userManagementConfiguration.permissions } }
                    twoFactorAuthRequired={member.user.twoFactorAuthRequired}
                    sendNumberVerificationRequest={sendTwoFactorRequest}
                    currentRole={values.role}
                    availableUserLicenses={data.userManagementConfiguration.availableLicenses}
                  />
                )}
              </LoaderOverlay>
            </DrawerBody>
            <DrawerFooter>
              <div className="d-flex justify-content-end align-items-center pr-5">
                <Button color="secondary" onClick={onCancel}>
                  Cancel
                </Button>
                <Button color="primary" disabled={!isValid || isSubmitting} type="submit">
                  Save
                </Button>
              </div>
            </DrawerFooter>
          </ControlledDrawer>
        </Form>
      )}
    </Formik>
  );
};

EditUserDrawer.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  memberID: PropTypes.string,
  onCancel: PropTypes.func,
  onSubmit: PropTypes.func,
  marketingDataDictionaryUrl: PropTypes.string
};
export default EditUserDrawer;

EditUserDrawer.defaultProps = {
  onSubmit: () => {}
};
